import React, { useRef, useState } from "react";
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_FILES,
  uploadAttachment,
  uploadAttachments,
  validateAttachmentFile,
  validateAttachmentFiles,
} from "../../api/attachments";
import "./AttachmentUploader.css";

/**
 * Shared file uploader for request forms.
 *
 * - With entityId: uploads immediately to the API.
 * - Without entityId: keeps files locally; parent uploads after create
 *   via uploadAttachments() or by calling the imperative onReady files.
 */
export default function AttachmentUploader({
  entityType,
  entityId = null,
  maxFiles = ATTACHMENT_MAX_FILES,
  accept = ATTACHMENT_ACCEPT,
  disabled = false,
  files: controlledFiles,
  onFilesChange,
  onUploaded,
  label = "المرفقات الداعمة",
}) {
  const inputRef = useRef(null);
  const [localFiles, setLocalFiles] = useState([]);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const files = controlledFiles ?? localFiles;

  function setFiles(next) {
    if (onFilesChange) onFilesChange(next);
    else setLocalFiles(next);
  }

  async function handleSelect(e) {
    setError("");
    const selected = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = "";
    if (!selected.length) return;

    const merged = [...files, ...selected].slice(0, maxFiles);
    const validationError = validateAttachmentFiles(merged);
    if (validationError) {
      setError(validationError);
      return;
    }
    for (const file of selected) {
      const err = validateAttachmentFile(file);
      if (err) {
        setError(err);
        return;
      }
    }

    if (!entityId) {
      setFiles(merged);
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      let result;
      if (selected.length === 1) {
        result = [
          await uploadAttachment(entityType, entityId, selected[0], setProgress),
        ];
      } else {
        result = await uploadAttachments(
          entityType,
          entityId,
          selected,
          setProgress,
        );
      }
      setFiles([]);
      onUploaded?.(result);
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر رفع الملفات",
      );
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }

  function removeFile(index) {
    setFiles(files.filter((_, i) => i !== index));
  }

  return (
    <div className="att-uploader" dir="rtl">
      <label className="att-uploader-label">{label}</label>
      <label
        className={`att-uploader-box ${disabled || uploading ? "is-disabled" : ""}`}
      >
        <span className="att-uploader-icon">+</span>
        <span className="att-uploader-text">
          اضغط لاختيار ملفات (حتى {maxFiles}) — PDF, صور, Word, Excel
        </span>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          disabled={disabled || uploading}
          onChange={handleSelect}
          className="att-uploader-input"
        />
      </label>

      {uploading && (
        <div className="att-uploader-progress" aria-valuenow={progress}>
          <div
            className="att-uploader-progress-bar"
            style={{ width: `${progress}%` }}
          />
          <span className="att-uploader-progress-label">{progress}%</span>
        </div>
      )}

      {error && <p className="att-uploader-error">{error}</p>}

      {!entityId && files.length > 0 && (
        <div className="att-uploader-pending">
          {files.map((file, index) => (
            <div key={`${file.name}-${index}`} className="att-uploader-chip">
              <span>{file.name}</span>
              <button
                type="button"
                className="att-uploader-chip-remove"
                onClick={() => removeFile(index)}
                aria-label="إزالة"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
